<template>
  <div class="w-full p-4 lens-comments-container">
    <!-- Filter Bar -->
    <div class="sticky top-4 z-30 border border-border-normal rounded-xl bg-white mb-4">
      <div class="w-full flex items-center justify-between p-3">
        <!-- Left side - Search Bar -->
        <div class="flex items-center flex-grow max-w-md">
          <div 
            class="search-input-container w-full flex items-center gap-1.5 pl-2.5 py-1.5 pr-2 rounded-md"
            :class="{ 'focused': searchInputFocused, 'has-value': searchQuery.length > 0 }"
            @click="$refs.searchInput.focus()"
          >
            <!-- Search Icon -->
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="18" 
              height="18" 
              viewBox="0 0 20 20" 
              fill="none"
              class="flex-shrink-0 text-text-subdued"
            >
              <path 
                d="M16.875 16.875L13.4388 13.4388M13.4388 13.4388C14.5321 12.3454 15.2083 10.835 15.2083 9.16667C15.2083 5.82995 12.5034 3.125 9.16667 3.125C5.82995 3.125 3.125 5.82995 3.125 9.16667C3.125 12.5034 5.82995 15.2083 9.16667 15.2083C10.835 15.2083 12.3454 14.5321 13.4388 13.4388Z" 
                stroke="currentColor" 
                stroke-width="1.5" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              />
            </svg>
            <!-- Input -->
            <input 
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="search-input flex-grow min-w-0 bg-transparent outline-none text-text-muted"
              placeholder="Search Comments..."
              autocomplete="off"
              spellcheck="false"
              @focus="searchInputFocused = true"
              @blur="searchInputFocused = false"
              @input="handleSearchInput"
            />
            <!-- Clear button or ⌘K indicator -->
            <transition name="fade" mode="out-in">
              <button 
                v-if="searchQuery.length > 0" 
                key="clear"
                class="flex-shrink-0 p-0.5 rounded text-text-subdued hover:text-text-muted"
                @click.stop="handleClearSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <div 
                v-else 
                key="shortcut"
                class="flex-shrink-0 flex items-center px-1 gap-px text-text-subdued"
              >
                <BaseText type="label" size="xs">⌘</BaseText>
                <BaseText type="body" size="xs">K</BaseText>
              </div>
            </transition>
          </div>
        </div>
        
        <!-- Right side - Comment Count + Sort Dropdown -->
        <div class="flex items-center gap-3">
          <!-- Comment Count -->
          <div class="flex items-center gap-2">
            <BaseText type="label" size="sm" class="text-text-muted">
              <!-- Show filtered/total when search is active and we have the initial total -->
              <template v-if="appliedSearchQuery && initialTotalComments > 0">
                {{ formatNumber(totalComments) }} / {{ formatNumber(initialTotalComments) }}
              </template>
              <template v-else>
                {{ formatNumber(totalComments || initialTotalComments || 0) }}
              </template>
              {{ totalComments === 1 ? 'Comment' : 'Comments' }}
            </BaseText>
            <!-- Spinner while loading -->
            <BaseLoadingSpinnerCircle v-if="loading" class="text-icon-disabled" :width="14" :height="14" />
          </div>
          
          <!-- Sort Dropdown -->
          <div class="relative" v-on-clickaway="() => showSortDropdown = false">
            <button 
              class="sort-btn flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md"
              :class="{ 'expanded': showSortDropdown }"
              @click="showSortDropdown = !showSortDropdown"
            >
              <SortIcon :width="18" :height="18" class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ currentSortLabel }}
              </BaseText>
            </button>
            <!-- Sort Dropdown Menu -->
            <transition name="fade">
              <div v-if="showSortDropdown" class="sort-dropdown flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                <button 
                  v-for="option in sortOptions" 
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ 'selected': sortBy === option.value }"
                  @click="setSortBy(option.value)"
                >
                  <BaseText type="body" size="sm" class="text-text-normal">
                    {{ option.name }}
                  </BaseText>
                  <NewCheckmarkIcon v-if="sortBy === option.value" class="ml-auto text-icon-normal" />
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Pinned Comments Section -->
    <div class="w-full flex flex-col py-2.5 gap-2.5 mb-3 border-t border-b border-border-normal">
      <div class="flex flex-col mx-2">
        <button 
          class="flex flex-row justify-between pr-3 items-center" 
          @click="() => { if (pinnedComments.length > 0) showPinnedSection = !showPinnedSection }"
          :class="{ 'cursor-default': pinnedComments.length === 0 }"
        >
          <div class="flex flex-row items-center">
            <div class="flex flex-row gap-3 px-4 py-1 items-center">
              <img src="../../assets/icons/unpinned.svg" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Pinned Comments
              </BaseText>
              <BaseText
                v-if="pinnedComments.length === 0"
                type="body"
                size="xs"
                class="text-neutral-500"
              >
                You don't have any comments pinned.
              </BaseText>
            </div>
          </div>
          <div class="flex flex-row">
            <transition>
              <div v-if="showPinnedSection && pinnedComments.length > 0">
                <div class="flex flex-row items-center gap-2">
                  <button 
                    class="flex flex-row py-1.5 px-2 gap-1.5 rounded-md transition-colors hover:bg-neutral-25"
                    @click.stop.prevent="exportPinnedCSV"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-icon-normal">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <BaseText type="label" size="sm" class="text-text-muted">
                      Export (CSV)
                    </BaseText>
                  </button>
                  <button 
                    class="flex flex-row py-1.5 px-2 gap-1.5 rounded-md transition-colors hover:bg-neutral-25"
                    @click.stop.prevent="copyAllPins"
                  >
                    <LayersCopyIcon class="text-icon-normal" />
                    <BaseText type="label" size="sm" class="text-text-muted">
                      Copy All Pins (Text)
                    </BaseText>
                  </button>
                  <button 
                    class="flex flex-row py-1.5 px-2 gap-1.5 rounded-md transition-colors hover:bg-neutral-25"
                    @click.stop.prevent="clearPins"
                  >
                    <UnpinIcon class="text-icon-normal" />
                    <BaseText type="label" size="sm" class="text-text-muted">
                      Clear Pins
                    </BaseText>
                  </button>
                </div>
              </div>
            </transition>
            <transition>
              <div 
                v-if="pinnedComments.length > 0"
                class="p-1.5 ml-2 rounded-md transition-colors hover:bg-neutral-25"
              >
                <ChevronIcon 
                  class="text-icon-normal transform" 
                  style="transition: transform 150ms ease-in-out"
                  :style="{ transform: showPinnedSection ? 'scaleY(-1)' : 'none' }" 
                /> 
              </div>
            </transition>
          </div>
        </button>
        <!-- Pinned Comments List -->
        <div 
          class="flex flex-col gap-1.5 px-1"
          :class="{ 'overflow-hidden': !showPinnedSection || pinnedComments.length === 0 }"
          :style="{ 
            maxHeight: showPinnedSection && pinnedComments.length > 0 ? '500px' : '0px',
            overflowY: showPinnedSection && pinnedComments.length > 0 ? 'auto' : 'hidden',
            transition: 'max-height 300ms cubic-bezier(0.4, 0, 0.2, 1)'
          }"
        >
          <transition-group name="pin-list" tag="div" class="flex flex-col gap-1.5">
            <div 
              v-for="(comment, index) in pinnedCommentsWithHighlights" 
              :key="`pinned-${comment.id}`"
              :class="{ 'mt-2': index === 0, 'mb-1': index === pinnedCommentsWithHighlights.length - 1 }"
              class="comment-row group rounded-lg p-2 flex flex-row gap-3 items-start transition-colors hover:bg-neutral-25"
            >
            <!-- Ad Thumbnail -->
            <div class="flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-neutral-100 cursor-pointer">
              <img 
                v-if="comment.adThumbnail" 
                :src="comment.adThumbnail" 
                class="w-full h-full object-cover"
                :alt="comment.adName"
              />
              <div v-else class="w-full h-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-text-subdued">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>

            <!-- Unpin Button -->
            <button 
              v-if="!unpinHoveredIndex || unpinHoveredIndex !== `pinned-${index}`"
              class="flex-shrink-0 group rounded-md p-1.5 items-center justify-center bg-primary-red-10 transition-colors hover:bg-primary-red-25" 
              @click="unpinComment(comment)"
              @mouseenter="unpinHoveredIndex = `pinned-${index}`"
              @mouseleave="unpinHoveredIndex = null"
            >
              <PinIcon class="text-primary-red-200" />
            </button>
            <button 
              v-else
              class="flex-shrink-0 group rounded-md p-1.5 items-center justify-center bg-primary-red-10 transition-colors hover:bg-primary-red-25" 
              @click="unpinComment(comment)"
              @mouseenter="unpinHoveredIndex = `pinned-${index}`"
              @mouseleave="unpinHoveredIndex = null"
            >
              <UnpinIcon class="transition-colors text-primary-red-200 group-hover:text-primary-red-300" />
            </button>

            <!-- Comment Bubble -->
            <div class="comment-bubble relative flex flex-grow rounded-lg px-3 py-2 bg-neutral-25 transition-colors group-hover:bg-neutral-50 min-w-0">
              <span class="comment-text text-text-muted pr-2" v-html="'&quot;' + comment.highlightedText + '&quot;'"></span>
            </div>

            <!-- Pills and Ad Details -->
            <div class="flex-shrink-0 flex flex-row items-center gap-2">
              <!-- Date Pill -->
              <BaseTooltip dark placement="top">
                <div slot="reference" class="pill flex items-center px-2 py-1 rounded-full bg-neutral-25 cursor-default">
                  <BaseText type="body" size="xs" class="text-text-subdued whitespace-nowrap">
                    {{ formatTimeAgo(comment.timestamp) }}
                  </BaseText>
                </div>
                <div slot="content" class="px-2 py-1 whitespace-nowrap">
                  <BaseText type="body" size="xs" class="text-white">
                    {{ formatExactTimestamp(comment.timestamp) }}
                  </BaseText>
                </div>
              </BaseTooltip>

              <!-- Likes Pill -->
              <BaseTooltip dark placement="top">
                <div slot="reference" class="pill flex items-center gap-1 pl-1 pr-2 py-1 rounded-full bg-neutral-25 cursor-default">
                  <div class="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                    </svg>
                  </div>
                  <BaseText type="body" size="xs" class="text-text-subdued">
                    {{ formatLikeCount(comment.likeCount) }}
                  </BaseText>
                </div>
                <div slot="content" class="px-2 py-1 whitespace-nowrap">
                  <BaseText type="body" size="xs" class="text-white">
                    {{ comment.likeCount }} Comment {{ comment.likeCount === 1 ? 'Like' : 'Likes' }}
                  </BaseText>
                </div>
              </BaseTooltip>

              <!-- Post Like Pill -->
              <BaseTooltip dark placement="top">
                <div 
                  slot="reference"
                  class="pill flex items-center gap-1 pl-1 pr-2 py-1 rounded-full bg-neutral-25 cursor-default"
                >
                  <div 
                    class="w-4 h-4 rounded-full flex items-center justify-center"
                    :class="comment.userPostLike ? 'bg-green-500' : 'bg-neutral-300'"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="10" height="10" 
                      viewBox="0 0 24 24" 
                      fill="white"
                      :class="{ 'transform rotate-180': !comment.userPostLike }"
                    >
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                    </svg>
                  </div>
                  <BaseText type="body" size="xs" class="text-text-subdued">
                    {{ comment.userPostLike ? 'Yes' : 'No' }}
                  </BaseText>
                </div>
                <div slot="content" class="px-2 py-1 whitespace-nowrap">
                  <BaseText type="body" size="xs" class="text-white">
                    If this commenter also liked the post
                  </BaseText>
                </div>
              </BaseTooltip>

              <!-- Ad Details Button -->
              <button 
                class="flex-shrink-0 flex items-center gap-1.5 py-1.5 px-2 rounded-md bg-white button-shadow"
                @click="handleAdClick(comment)"
              >
                <AdDetailsAnimatedIcon :width="20" :height="20" :isHovered="false" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Ad Details
                </BaseText>
              </button>
            </div>
          </div>
          </transition-group>
        </div>
      </div>
    </div>

    <!-- Skeleton Loading State -->
    <transition name="skeleton-fade">
      <div v-if="loading" key="skeleton" class="flex flex-col gap-1.5">
        <div 
          v-for="i in limit" 
          :key="`skeleton-${i}`"
          class="skeleton-row rounded-lg p-2 flex flex-row gap-3 items-center"
          :style="{ animationDelay: `${i * 50}ms` }"
        >
          <!-- Skeleton Thumbnail -->
          <div class="skeleton-pulse flex-shrink-0 w-10 h-10 rounded-md bg-neutral-100"></div>
          
          <!-- Skeleton Pin Button -->
          <div class="skeleton-pulse flex-shrink-0 w-7 h-7 rounded-md bg-neutral-100"></div>
          
          <!-- Skeleton Comment Bubble (varying heights) -->
          <div 
            class="skeleton-pulse flex-grow rounded-lg bg-neutral-100"
            :style="{ height: skeletonHeights[i % skeletonHeights.length] + 'px' }"
          ></div>
          
          <!-- Skeleton Pills -->
          <div class="flex-shrink-0 flex flex-row items-center gap-2">
            <div class="skeleton-pulse w-14 h-6 rounded-full bg-neutral-100"></div>
            <div class="skeleton-pulse w-10 h-6 rounded-full bg-neutral-100"></div>
            <div class="skeleton-pulse w-20 h-6 rounded-full bg-neutral-100"></div>
            <!-- Skeleton Ad Details Button -->
            <div class="skeleton-pulse w-24 h-8 rounded-md bg-neutral-100"></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Empty State (only show when not loading and no comments) -->
    <div v-if="!loading && !comments.length" class="flex flex-col items-center justify-center mt-24">
      <div class="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-600 mb-1">No Comments Found</p>
      <p class="text-sm text-gray-500 text-center max-w-xs">
        No comments found for this search, try something else.
      </p>
    </div>

    <!-- Comments List (only show when not loading and has comments) -->
    <transition-group v-if="!loading && comments.length" name="comment-list" tag="div" class="flex flex-col gap-1.5 relative">
      <div 
        v-for="(comment, index) in commentsWithHighlights" 
        :key="comment.id || `comment-${index}`"
        class="comment-row group rounded-lg p-2 flex flex-row gap-3 items-start transition-colors hover:bg-neutral-25"
        :style="{ zIndex: hoveredThumbnailIndex === index ? 20 : 1 }"
      >
        <!-- Ad Thumbnail with Hover Preview -->
        <div 
          class="relative flex-shrink-0"
          @mouseenter="hoveredThumbnailIndex = index"
          @mouseleave="hoveredThumbnailIndex = null"
          @click.stop.prevent="handleThumbnailClick(comment, index)"
        >
          <div class="w-10 h-10 rounded-md overflow-hidden bg-neutral-100 cursor-pointer">
            <img 
              v-if="comment.adThumbnail" 
              :src="comment.adThumbnail" 
              class="w-full h-full object-cover"
              :alt="comment.adName"
            />
            <div v-else class="w-full h-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-text-subdued">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>
          </div>
          <!-- Hover Preview (positioned like Spyder Hooks / Creative Tests) -->
          <transition name="fade">
            <div 
              v-if="hoveredThumbnailIndex === index && comment.assetSrc" 
              class="absolute left-full top-1/2 transform -translate-y-1/2 pl-2" 
              style="z-index: 50001"
              @mouseenter="hoveredThumbnailIndex = null"
            >
              <BasePopupVideoPreview 
                v-if="comment.assetType === 'video'"
                :ref="`videoPreview-${index}`"
                class="relative"
                :title="comment.adName"
                :video-src="comment.assetSrc"
                :attempt-manual-play="attemptManualPreviewPlayCounter"
              />
              <BasePopupThumbnailPreview 
                v-else
                class="relative"
                :title="comment.adName"
                :thumbnail-src="comment.assetSrc"
              />
            </div>
          </transition>
        </div>

        <!-- Pin/Unpin Button -->
        <button 
          v-if="!isCommentPinned(comment)"
          class="flex-shrink-0 group rounded-md p-1.5 items-center justify-center transition-colors hover:bg-neutral-50" 
          @click="pinComment(comment)"
        >
          <PinIcon class="flex-shrink-0 text-icon-normal transition-colors group-hover:text-icon-muted" />
        </button>
        <button 
          v-else
          class="flex-shrink-0 group rounded-md p-1.5 items-center justify-center bg-primary-red-10 transition-colors hover:bg-primary-red-25" 
          @click="unpinComment(comment)"
          @mouseenter="unpinHoveredIndex = `main-${index}`"
          @mouseleave="unpinHoveredIndex = null"
        >
          <PinIcon v-if="unpinHoveredIndex !== `main-${index}`" class="text-primary-red-200" />
          <UnpinIcon v-else class="transition-colors text-primary-red-200 group-hover:text-primary-red-300" />
        </button>

        <!-- Comment Bubble with Copy -->
        <div 
          class="comment-bubble relative flex flex-grow rounded-lg px-3 py-2 bg-neutral-25 transition-colors group-hover:bg-neutral-50 min-w-0"
          @mouseenter="hoveredCommentIndex = index"
          @mouseleave="hoveredCommentIndex = null"
        >
          <span 
            class="comment-text text-text-muted pr-2"
            v-html="'&quot;' + comment.highlightedText + '&quot;'"
          ></span>
          <!-- Copy Button - positioned at right edge, vertically centered -->
          <transition>
            <button 
              v-if="hoveredCommentIndex === index && copiedCommentIndex !== index"
              class="copy-btn copy-btn-border absolute right-1 flex flex-row gap-1 items-center bg-white hover:bg-neutral-25 px-2 py-1 rounded-md"
              @click.stop="copyComment(comment.text, index)"
            >
              <LayersCopyIcon class="text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Copy Text
              </BaseText>
            </button>
            <div 
              v-else-if="hoveredCommentIndex === index && copiedCommentIndex === index"
              class="copy-btn absolute right-1 flex flex-row gap-1 items-center bg-neutral-700 pl-1.5 pr-2 py-1 rounded-md cursor-default"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="text-white">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <BaseText type="body" size="sm" class="text-white">
                Copied!
              </BaseText>
            </div>
          </transition>
        </div>

        <!-- Right Side: Pills + Ad Details -->
        <div class="flex-shrink-0 flex flex-row items-center gap-2">
          <!-- Date Pill -->
          <BaseTooltip dark placement="top">
            <div slot="reference" class="pill flex items-center px-2 py-1 rounded-full bg-neutral-25 cursor-default">
              <BaseText type="body" size="xs" class="text-text-subdued whitespace-nowrap">
                {{ formatTimeAgo(comment.timestamp) }}
              </BaseText>
            </div>
            <div slot="content" class="px-2 py-1 whitespace-nowrap">
              <BaseText type="body" size="xs" class="text-white">
                {{ formatExactTimestamp(comment.timestamp) }}
              </BaseText>
            </div>
          </BaseTooltip>

          <!-- Likes Pill -->
          <BaseTooltip dark placement="top">
            <div slot="reference" class="pill flex items-center gap-1 pl-1 pr-2 py-1 rounded-full bg-neutral-25 cursor-default">
              <div class="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                </svg>
              </div>
              <BaseText type="body" size="xs" class="text-text-subdued">
                {{ formatLikeCount(comment.likeCount) }}
              </BaseText>
            </div>
            <div slot="content" class="px-2 py-1 whitespace-nowrap">
              <BaseText type="body" size="xs" class="text-white">
                {{ comment.likeCount }} Comment {{ comment.likeCount === 1 ? 'Like' : 'Likes' }}
              </BaseText>
            </div>
          </BaseTooltip>

          <!-- Post Like Pill -->
          <BaseTooltip dark placement="top">
            <div 
              slot="reference"
              class="pill flex items-center gap-1 pl-1 pr-2 py-1 rounded-full bg-neutral-25 cursor-default"
            >
              <div 
                class="w-4 h-4 rounded-full flex items-center justify-center"
                :class="comment.userPostLike ? 'bg-green-500' : 'bg-neutral-300'"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="10" height="10" 
                  viewBox="0 0 24 24" 
                  fill="white"
                  :class="{ 'transform rotate-180': !comment.userPostLike }"
                >
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                </svg>
              </div>
              <BaseText type="body" size="xs" class="text-text-subdued">
                {{ comment.userPostLike ? 'Yes' : 'No' }}
              </BaseText>
            </div>
            <div slot="content" class="px-2 py-1 whitespace-nowrap">
              <BaseText type="body" size="xs" class="text-white">
                If this commenter also liked the post
              </BaseText>
            </div>
          </BaseTooltip>

          <!-- Ad Details Button -->
          <button 
            class="flex-shrink-0 flex items-center gap-1.5 py-1.5 px-2 rounded-md bg-white button-shadow"
            @mouseenter="hoveredDetailsIndex = index"
            @mouseleave="hoveredDetailsIndex = null"
            @click="handleAdClick(comment)"
          >
            <AdDetailsAnimatedIcon :width="20" :height="20" :isHovered="hoveredDetailsIndex === index" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Ad Details
            </BaseText>
          </button>
        </div>
      </div>
    </transition-group>

    <!-- Pagination (hidden during loading) -->
    <TablePagination 
      v-if="totalPages > 0 && comments.length && !loading"
      :showing="comments.length"
      :table-pagination="tablePaginationData"
      @updatePagination="handlePaginationUpdate"
    />

  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import firebase from '@/api/config/FirebaseConfig'
import { mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import TablePagination from '../../components/lens/table/TablePagination.vue'
import BasePopupVideoPreview from '../../components/globals/BasePopupVideoPreview.vue'
import BasePopupThumbnailPreview from '../../components/globals/BasePopupThumbnailPreview.vue'
import BaseLoadingSpinnerCircle from '../../components/globals/BaseLoadingSpinnerCircle.vue'
import AdDetailsAnimatedIcon from '../../components/globals/Icons/AdDetailsAnimatedIcon.vue'
import LayersCopyIcon from '../../components/globals/Icons/LayersCopyIcon.vue'
import SortIcon from '../../components/globals/Icons/SortIcon.vue'
import NewCheckmarkIcon from '../../components/globals/Icons/NewCheckmarkIcon.vue'
import BaseText from '../../components/globals/BaseText.vue'
import BaseTooltip from '../../components/globals/BaseTooltip.vue'
import PinIcon from '../../components/globals/Icons/SpyderV2Icons/PinIcon.vue'
import UnpinIcon from '../../components/globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'

// Static sort configuration - avoids object recreation on every fetch
const SORT_MAP = Object.freeze({
  mostLikes: { sort_by: 'like_count', sort_order: 'desc' },
  newest: { sort_by: 'created_time', sort_order: 'desc' },
  oldest: { sort_by: 'created_time', sort_order: 'asc' }
})

export default {
  name: 'LensCommentsView',
  mixins: [clickaway],
  components: {
    TablePagination,
    BasePopupVideoPreview,
    BasePopupThumbnailPreview,
    BaseLoadingSpinnerCircle,
    AdDetailsAnimatedIcon,
    LayersCopyIcon,
    SortIcon,
    NewCheckmarkIcon,
    BaseText,
    BaseTooltip,
    PinIcon,
    UnpinIcon,
    ChevronIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      comments: [],
      currentPage: 1,
      totalPages: 1,
      totalComments: 0,
      initialTotalComments: null, // Stores unfiltered total (set once on first load)
      cachedInitialComments: null, // Cache first page of unfiltered comments for instant restore
      cachedInitialPages: null, // Cache total pages for unfiltered state
      limit: 20,
      // Skeleton loading heights for natural variation
      skeletonHeights: [40, 56, 40, 72, 48, 56, 40, 64, 40, 48],
      // Hover states
      hoveredCommentIndex: null,
      copiedCommentIndex: null,
      hoveredDetailsIndex: null,
      hoveredThumbnailIndex: null,
      attemptManualPreviewPlayCounter: 0,
      // Search state
      searchQuery: '',
      appliedSearchQuery: '', // Only updates after API returns
      searchInputFocused: false,
      searchDebounceTimer: null,
      // Sort state
      sortBy: 'mostLikes',
      showSortDropdown: false,
      sortOptions: [
        { name: 'Most Likes', value: 'mostLikes' },
        { name: 'Newest', value: 'newest' },
        { name: 'Oldest', value: 'oldest' }
      ],
      // Pin state
      pinnedComments: [],
      showPinnedSection: true,
      unpinHoveredIndex: null
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    tablePaginationData () {
      return {
        limit: this.limit,
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        totalItems: this.totalComments
      }
    },
    currentSortLabel () {
      const option = this.sortOptions.find(o => o.value === this.sortBy)
      return option ? option.name : 'Most Likes'
    },
    // Cached search regex - only uses appliedSearchQuery (after API returns)
    searchRegex () {
      const query = this.appliedSearchQuery.trim()
      if (!query) return null
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return new RegExp(`(${escaped})`, 'gi')
    },
    // Pre-computed highlighted text for all comments - cached by Vue
    commentsWithHighlights () {
      return this.comments.map(comment => ({
        ...comment,
        highlightedText: this.computeHighlight(comment.text)
      }))
    },
    // Pre-computed highlighted text for pinned comments
    pinnedCommentsWithHighlights () {
      return this.pinnedComments.map(comment => ({
        ...comment,
        highlightedText: this.computeHighlight(comment.text)
      }))
    },
    // Memoized Set for O(1) pinned comment lookups
    pinnedCommentIds () {
      return new Set(this.pinnedComments.map(c => c.id))
    },
    // Secondary lookup map for fallback matching (ad_id + text)
    pinnedCommentKeys () {
      return new Set(this.pinnedComments.map(c => `${c.ad_id}|${c.text}`))
    }
  },
  watch: {
    lensId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.currentPage = 1
          this.fetchComments()
        }
      }
    }
  },
  mounted () {
    this.fetchPinnedComments()
  },
  beforeDestroy () {
    // Clean up debounce timer to prevent memory leaks
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  },
  methods: {
    // Pin functionality methods
    isCommentPinned (comment) {
      // O(1) lookup using memoized Sets
      if (comment.id && this.pinnedCommentIds.has(comment.id)) return true
      // Fallback: check by ad_id + text combination
      return this.pinnedCommentKeys.has(`${comment.ad_id}|${comment.text}`)
    },
    async fetchPinnedComments () {
      try {
        const db = firebase.firestore()
        const user = this.getUser
        if (!user) return

        const querySnapshot = await db.collection('pinned-comments')
          .where('userId', '==', user.user_id)
          .where('lensId', '==', this.lensId)
          .get()

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0]
          const data = doc.data()
          if (data.comments && Array.isArray(data.comments)) {
            this.pinnedComments = data.comments
          }
        }
      } catch (e) {
        console.error('[LensComments] Error fetching pinned comments:', e)
      }
    },
    async pinComment (comment) {
      try {
        const db = firebase.firestore()
        const user = this.getUser
        if (!user) return

        // Check if already pinned
        if (this.isCommentPinned(comment)) return

        // Sanitize comment - Firebase doesn't allow undefined values
        // Use the original comment id if available for consistent matching
        const sanitizedComment = {
          id: comment.id || `${comment.ad_id}-${comment.text?.substring(0, 20) || Date.now()}`,
          text: comment.text || '',
          timestamp: comment.timestamp || Date.now(),
          likeCount: comment.likeCount || 0,
          userPostLike: comment.userPostLike || false,
          ad_id: comment.ad_id || null,
          creative_id: comment.creative_id || null,
          creative_hash: comment.creative_hash || null,
          adName: comment.adName || '',
          adThumbnail: comment.adThumbnail || null,
          adVideo: comment.adVideo || null,
          assetType: comment.assetType || 'image',
          assetSrc: comment.assetSrc || comment.adThumbnail || null
        }

        const docRef = db.collection('pinned-comments')
          .where('userId', '==', user.user_id)
          .where('lensId', '==', this.lensId)

        const querySnapshot = await docRef.get()

        if (!querySnapshot.empty) {
          // Document exists, update it
          const doc = querySnapshot.docs[0]
          await doc.ref.update({
            comments: firebase.firestore.FieldValue.arrayUnion(sanitizedComment)
          })
        } else {
          // Create new document
          await db.collection('pinned-comments').add({
            userId: user.user_id,
            lensId: this.lensId,
            comments: [sanitizedComment]
          })
        }

        // Update local state with sanitized comment
        this.pinnedComments = [...this.pinnedComments, sanitizedComment]
      } catch (error) {
        console.error('[LensComments] Error pinning comment:', error)
      }
    },
    async unpinComment (comment) {
      try {
        const db = firebase.firestore()
        const user = this.getUser
        if (!user) return

        const docRef = db.collection('pinned-comments')
          .where('userId', '==', user.user_id)
          .where('lensId', '==', this.lensId)

        const querySnapshot = await docRef.get()

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0]
          // Find and remove the comment with matching id
          const currentComments = doc.data().comments || []
          const updatedComments = currentComments.filter(c => c.id !== comment.id)
          await doc.ref.update({
            comments: updatedComments
          })
        }

        // Update local state
        this.pinnedComments = this.pinnedComments.filter(c => c.id !== comment.id)
      } catch (error) {
        console.error('[LensComments] Error unpinning comment:', error)
      }
    },
    async clearPins () {
      if (!this.pinnedComments.length) return

      try {
        const db = firebase.firestore()
        const user = this.getUser
        if (!user) return

        const docRef = db.collection('pinned-comments')
          .where('userId', '==', user.user_id)
          .where('lensId', '==', this.lensId)

        const querySnapshot = await docRef.get()

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0]
          await doc.ref.update({
            comments: []
          })
        }

        // Update local state
        this.pinnedComments = []
      } catch (error) {
        console.error('[LensComments] Error clearing pins:', error)
      }
    },
    copyAllPins () {
      if (!this.pinnedComments.length) return
      const allPinnedComments = this.pinnedComments
        .map(comment => comment.text.trim())
        .join('\n')
      navigator.clipboard.writeText(allPinnedComments)
    },
    exportPinnedCSV () {
      if (!this.pinnedComments.length) return
      
      const headers = ['Comment', 'Date', 'Likes', 'User Post Like', 'Ad Name', 'Ad ID']
      const rows = this.pinnedComments.map(comment => [
        `"${(comment.text || '').replace(/"/g, '""')}"`,
        `"${new Date(comment.timestamp).toISOString()}"`,
        comment.likeCount || 0,
        comment.userPostLike ? 'Yes' : 'No',
        `"${(comment.adName || '').replace(/"/g, '""')}"`,
        comment.ad_id || ''
      ])

      const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${this.lens?.name || 'lens'}-pinned-comments.csv`.replace(/[^a-zA-Z0-9-_.]/g, '-')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    async fetchComments () {
      this.loading = true
      
      try {
        const sortParams = SORT_MAP[this.sortBy] || SORT_MAP.mostLikes

        const requestParams = {
          limit: this.limit,
          page: this.currentPage,
          ...sortParams,
          // Include search query if present
          ...(this.searchQuery.trim() && { q: this.searchQuery.trim() })
        }

        const response = await LensAPI.Lens.getAllLensComments(this.lensId, requestParams)
        
        let commentsArray = []
        if (Array.isArray(response)) {
          commentsArray = response
        } else if (response?.comments && Array.isArray(response.comments)) {
          commentsArray = response.comments
        } else if (response?.data && Array.isArray(response.data)) {
          commentsArray = response.data
        }

        if (response?.total !== undefined) {
          this.totalComments = response.total
          this.totalPages = Math.ceil(response.total / this.limit)
        } else if (response?.pagination) {
          this.totalComments = response.pagination.total || 0
          this.totalPages = response.pagination.totalPages || Math.ceil(this.totalComments / this.limit)
        } else {
          this.totalComments = commentsArray.length
          this.totalPages = 1
        }

        // Store initial total on first load (when no search is active)
        if (!this.initialTotalComments && !requestParams.q) {
          this.initialTotalComments = this.totalComments
        }

        this.comments = commentsArray.map(comment => {
          // Determine asset type and source (matching Creative Tests pattern)
          const videoSrc = comment.video_url_source || comment.ad_video_url || comment.video_url || ''
          const imageSrc = comment.ad_image_url || comment.ad_thumbnail || comment.thumbnail || ''
          const assetType = videoSrc?.length ? 'video' : 'image'
          const assetSrc = assetType === 'video' ? videoSrc : imageSrc

          return {
            // Unique identifier for the comment
            id: comment.id,
            text: comment.message || comment.text || '',
            timestamp: this.parseTimestamp(comment.created_time || comment.timestamp),
            likeCount: comment.like_count ?? 0,
            userPostLike: comment.user_likes ?? comment.user_post_like ?? false,
            // Ad identifiers for details drawer
            ad_id: comment.ad_id,
            creative_id: comment.creative_id,
            creative_hash: comment.creative_hash,
            // Ad display data (matching Creative Tests pattern)
            adName: comment.ad_name || '',
            adThumbnail: imageSrc || null,
            adVideo: videoSrc || null,
            assetType,
            assetSrc
          }
        })

        // Cache the first page of unfiltered results for instant restore on search clear
        // Must be AFTER this.comments assignment to cache the new data
        if (!requestParams.q && this.currentPage === 1) {
          this.cachedInitialComments = [...this.comments]
          this.cachedInitialPages = this.totalPages
        }

        // Update applied search query AFTER results arrive (for highlighting)
        this.appliedSearchQuery = this.searchQuery
      } catch (error) {
        console.error('[LensComments] Error fetching lens comments:', error)
        this.comments = []
        this.totalComments = 0
        this.totalPages = 1
      } finally {
        this.loading = false
      }
    },
    parseTimestamp (timestamp) {
      if (!timestamp) return Date.now()
      if (typeof timestamp === 'number' && timestamp < 10000000000) {
        return timestamp * 1000
      }
      return new Date(timestamp).getTime()
    },
    handlePaginationUpdate ({ limit, currentPage }) {
      this.limit = limit
      this.currentPage = currentPage
      this.fetchComments()
    },
    setSortBy (value) {
      this.sortBy = value
      this.showSortDropdown = false
      // Invalidate cache since sort order changed
      this.cachedInitialComments = null
      this.cachedInitialPages = null
      // Reset to page 1 and re-fetch with new sort order
      this.currentPage = 1
      this.fetchComments()
    },
    handleSearchInput () {
      // Debounce search to avoid excessive API calls
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchComments()
      }, 300)
    },
    handleClearSearch () {
      this.searchQuery = ''
      this.appliedSearchQuery = '' // Clear highlights immediately when clearing
      this.currentPage = 1
      
      // Use cached initial comments for instant restore (if available and on page 1)
      if (this.cachedInitialComments && this.cachedInitialComments.length > 0) {
        this.comments = [...this.cachedInitialComments]
        this.totalComments = this.initialTotalComments
        this.totalPages = this.cachedInitialPages
        // Don't need to fetch - we have the cached data
        return
      }
      
      // Fallback to fetch if no cache
      this.fetchComments()
    },
    computeHighlight (text) {
      if (!text) return ''
      const escapedText = this.escapeHtml(text)
      
      // Use cached regex from computed property
      if (!this.searchRegex) return escapedText
      
      // Reset regex lastIndex for global regex reuse
      this.searchRegex.lastIndex = 0
      return escapedText.replace(this.searchRegex, '<mark class="search-highlight">$1</mark>')
    },
    escapeHtml (text) {
      if (!text) return ''
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    copyComment (text, index) {
      navigator.clipboard.writeText(text.trim())
      this.copiedCommentIndex = index
      setTimeout(() => {
        this.copiedCommentIndex = null
      }, 2000)
    },
    formatTimeAgo (timestamp) {
      if (!timestamp) return ''
      const now = Date.now()
      const diff = now - timestamp
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 60) return `${minutes}m ago`
      if (hours < 24) return `${hours}h ago`
      if (days < 7) return `${days}d ago`
      if (days < 30) return `${Math.floor(days / 7)}w ago`
      return `${Math.floor(days / 30)}mo ago`
    },
    formatExactTimestamp (timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
    },
    formatLikeCount (count) {
      if (!count) return '0'
      if (count >= 1000) {
        return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
      }
      return count.toString()
    },
    formatNumber (num) {
      if (!num) return '0'
      return num.toLocaleString()
    },
    handleAdClick (comment) {
      this.$emit('showAdDetails', {
        ad_id: comment.ad_id,
        creative_id: comment.creative_id,
        creative_hash: comment.creative_hash
      })
    },
    handleThumbnailClick (comment, index) {
      // Check if video preview is showing and playback is blocked
      const videoPreviewRef = this.$refs[`videoPreview-${index}`]
      const videoPreview = Array.isArray(videoPreviewRef) ? videoPreviewRef[0] : videoPreviewRef
      const isPlaybackBlocked = videoPreview && typeof videoPreview.getIsPlaybackBlocked === 'function' && videoPreview.getIsPlaybackBlocked()
      
      if (this.hoveredThumbnailIndex !== null && comment.assetType === 'video' && isPlaybackBlocked) {
        // First click: just trigger video playback, don't open drawer
        this.attemptManualPreviewPlayCounter++
        return
      }
      
      // Subsequent clicks: open ad details drawer
      this.handleAdClick(comment)
      this.hoveredThumbnailIndex = null
    }
  }
}
</script>

<style scoped>
.button-shadow {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.pill {
  border: 1px solid #ECEFF3;
}

.copy-btn-border {
  border: 1px solid #DFE1E7;
}

.copy-btn {
  top: 50%;
  transform: translateY(-50%);
}

/* Search Input Styles */
.search-input-container {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #EBEFF3;
  transition: box-shadow 100ms ease-in-out;
  cursor: text;
}

.search-input-container:hover {
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #808899;
}

.search-input-container.focused {
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #808899;
}

.search-input {
  font-feature-settings: 'ss01' on, 'cv10' on, 'liga' off, 'calt' off;
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 20px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0;
  margin: 0;
  border: none;
}

.search-input::placeholder {
  color: #5E6678;
}

.search-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}

/* Comment Text Styles */
.comment-text {
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

/* Search Highlight Styles - using ::v-deep for v-html content */
::v-deep .search-highlight {
  background-color: #DBEAFE;
  color: #1E40AF;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

/* Skeleton Loading Styles */
.skeleton-pulse {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Skeleton Fade Transition */
.skeleton-fade-enter-active {
  transition: opacity 200ms ease-out;
}
.skeleton-fade-leave-active {
  transition: opacity 150ms ease-in;
}
.skeleton-fade-enter-from,
.skeleton-fade-leave-to {
  opacity: 0;
}

/* Sort Button Styles */
.sort-btn {
  transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
  background-color: transparent;
  box-shadow: none;
  white-space: nowrap;
}

.sort-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
}

.sort-btn.expanded {
  box-shadow: none;
  background-color: #F6F8FA;
}

/* Sort Dropdown */
.sort-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 140px;
  z-index: 50;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 100ms ease;
}

.dropdown-item:hover {
  background-color: #F6F8FA;
}

.dropdown-item.selected {
  background-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}

.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}

.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 150ms ease-in-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.copied-animation {
  animation: fadeIn 150ms ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Apple-like pin transitions */
.pin-list-enter-active {
  animation: pinEnter 280ms cubic-bezier(0.2, 0, 0, 1);
}

.pin-list-leave-active {
  animation: pinLeave 220ms cubic-bezier(0.4, 0, 1, 1);
}

.pin-list-move {
  transition: transform 280ms cubic-bezier(0.2, 0, 0, 1);
}

@keyframes pinEnter {
  0% {
    opacity: 0;
    transform: scale(0.96) translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes pinLeave {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.96);
  }
}

/* Comment list transitions */
.comment-list-enter-active {
  animation: commentEnter 250ms cubic-bezier(0.2, 0, 0, 1);
}

.comment-list-leave-active {
  animation: commentLeave 200ms cubic-bezier(0.4, 0, 1, 1);
  position: absolute;
  width: 100%;
}

.comment-list-move {
  transition: transform 280ms cubic-bezier(0.2, 0, 0, 1);
}

@keyframes commentEnter {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes commentLeave {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.98) translateY(-4px);
  }
}

/* Container z-index containment */
.lens-comments-container {
  position: relative;
  isolation: isolate;
}

/* Ensure comment rows stay below sticky filter bar */
.comment-row {
  position: relative;
  z-index: 1;
}

/* Pills should not create new stacking context that escapes */
.pill {
  position: relative;
  z-index: 1;
}
</style>
